import React, { useState, useEffect, useMemo, useRef } from 'react';
import init, { decrypt_ciphertext_with_randomness, decode_transfer_proof } from '../pkg/confidential_transfer_proof_generation.js';
import { getChainDisplayNameForRequest, getLogicalChainKey } from '../config/chains';
import { getRequestApplication, getApplicationDisplayName } from '../config/applications';
import { roundDecimal } from '../utils/numbers';
import { obtainUnlockedIbeKeyForIdentity } from '../services/obtainIbeKey';
import { decryptTransactionAmount, getRoleForUser, getEncryptedRandomnessForRole } from '../utils/transactionDecryption';
import SelectField from './SelectField';
import Modal from './Modal';
import { toUserFriendlyError } from '../utils/friendlyError';
import './DecryptionModal.css';
import './Modal.css';

const AccumulationModal = ({
  userAddress,
  transactions,
  auditorData,
  onClose,
  onBulkDecryptionComplete
}) => {
  const [selectedChainId, setSelectedChainId] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null); // null = all applications
  const [step, setStep] = useState('select'); // 'select' | 'request' | 'polling' | 'decrypting' | 'complete'
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [wasmModule, setWasmModule] = useState(null);
  const [decryptProgress, setDecryptProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState(null); // { amountsMap, received, sent, net, chainName, token, application }
  const pollStartedAtRef = useRef(null);
  const ibeKeyCacheRef = useRef(new Map());
  const pollAbortRef = useRef(null);

  const groups = useMemo(() => {
    const map = new Map();
    for (const tx of transactions) {
      const token = tx.details?.token || 'N/A';
      const logicalChainKey = getLogicalChainKey(tx);
      const key = `${logicalChainKey}-${token}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          logicalChainKey,
          chainName: getChainDisplayNameForRequest(tx),
          token,
          txs: []
        });
      }
      map.get(key).txs.push(tx);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.chainName + a.token).localeCompare(b.chainName + b.token)
    );
  }, [transactions]);

  const chains = useMemo(() => {
    const seen = new Map();
    for (const g of groups) {
      if (!seen.has(g.logicalChainKey)) {
        seen.set(g.logicalChainKey, { chainId: g.logicalChainKey, chainName: g.chainName });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.chainName.localeCompare(b.chainName));
  }, [groups]);

  const tokensForSelectedChain = useMemo(() => {
    if (selectedChainId == null || selectedChainId === '') return [];
    return groups
      .filter((g) => g.logicalChainKey === selectedChainId)
      .map((g) => ({ token: g.token, count: g.txs.length }));
  }, [groups, selectedChainId]);

  const selectedGroup = selectedChainId != null && selectedChainId !== '' && selectedToken
    ? `${selectedChainId}-${selectedToken}`
    : null;

  const selectedGroupData = useMemo(
    () => groups.find((g) => g.key === selectedGroup) || null,
    [groups, selectedGroup]
  );

  const applicationsForSelectedGroup = useMemo(() => {
    if (!selectedGroupData) return [];
    const counts = new Map();
    for (const tx of selectedGroupData.txs) {
      const app = getRequestApplication(tx);
      counts.set(app, (counts.get(app) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([application, count]) => ({
        application,
        label: getApplicationDisplayName(application),
        count
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedGroupData]);

  const scopedTxs = useMemo(() => {
    if (!selectedGroupData) return [];
    if (!selectedApplication) return selectedGroupData.txs;
    return selectedGroupData.txs.filter(
      (tx) => getRequestApplication(tx).toLowerCase() === selectedApplication.toLowerCase()
    );
  }, [selectedGroupData, selectedApplication]);

  const scopeLabel = selectedGroupData
    ? [
        selectedGroupData.chainName,
        selectedGroupData.token,
        selectedApplication ? getApplicationDisplayName(selectedApplication) : null
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  useEffect(() => {
    const initWasm = async () => {
      try {
        await init();
        setWasmModule({ decrypt_ciphertext_with_randomness, decode_transfer_proof });
      } catch (err) {
        console.error('Failed to initialize WASM:', err);
        setError('Failed to initialize decryption module');
      }
    };
    initWasm();
  }, []);

  // Tick elapsed time once per second while waiting — independent of poll attempts.
  useEffect(() => {
    const waiting = step === 'request' || step === 'polling';
    if (!waiting) {
      pollStartedAtRef.current = null;
      return undefined;
    }
    if (pollStartedAtRef.current == null) {
      pollStartedAtRef.current = Date.now();
      setPollingStatus({ elapsed: 0 });
    }
    const id = setInterval(() => {
      const started = pollStartedAtRef.current || Date.now();
      setPollingStatus({ elapsed: Date.now() - started });
    }, 1000);
    return () => clearInterval(id);
  }, [step]);

  const ensureIbeKey = async (pepIdentity, { which, tx } = {}) => {
    const cacheKey = String(pepIdentity).toLowerCase();
    if (ibeKeyCacheRef.current.has(cacheKey)) {
      return ibeKeyCacheRef.current.get(cacheKey);
    }

    setStep('polling');
    console.log('[AccumulationModal] Requesting decryption key for identity:', {
      which,
      pepIdentityPreview: `${String(pepIdentity).substring(0, 24)}…`,
      txId: tx?.id
    });

    const pollAbort = new AbortController();
    pollAbortRef.current = pollAbort;

    const unlocked = await obtainUnlockedIbeKeyForIdentity({
      pepIdentity,
      auditorIdentity: auditorData.uid || auditorData.id,
      secpPublicKey: auditorData.publicKey,
      lookupHints: {
        chainTxHash: tx?.transactionHash || tx?.txHash || null,
        extraAuditorIds: [auditorData.grantId, auditorData.id].filter(
          (x) => typeof x === 'string' && x.length > 0
        ),
        requestId: tx?.id || null
      },
      abortSignal: pollAbort.signal,
      onPhase: (phase) => {
        if (phase === 'polling') setStep('polling');
      }
    });

    ibeKeyCacheRef.current.set(cacheKey, unlocked);
    setStep('decrypting');
    return unlocked;
  };

  const handleRequestKey = async () => {
    if (!selectedGroup) return;
    const groupTxs = scopedTxs;
    if (!groupTxs[0]) return;

    setError(null);
    setLoading(true);
    ibeKeyCacheRef.current = new Map();

    try {
      if (!auditorData?.publicKey) {
        throw new Error('Auditor public key not found. Please contact administrator.');
      }
      const auditorIdentity = auditorData.uid || auditorData.id;
      if (!auditorIdentity) {
        throw new Error('Auditor UID not found. Please contact administrator.');
      }

      setStep('request');

      // Prefetch unique active identities so most decrypts hit a warm cache.
      // Queued identities (pk rotation) are fetched on demand during decrypt.
      const seen = new Set();
      for (const tx of groupTxs) {
        const { active } = getEncryptedRandomnessForRole(tx, userAddress);
        const pepIdentity = active.pepIdentity;
        if (!pepIdentity) continue;
        const key = String(pepIdentity).toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        await ensureIbeKey(pepIdentity, { which: 'active', tx });
      }

      if (seen.size === 0) {
        throw new Error('No IBE identities found for the selected transactions');
      }

      setStep('decrypting');
      await runBulkDecrypt();
    } catch (err) {
      console.error('Key request error:', err);
      setError(toUserFriendlyError(err, 'Could not request a decryption key. Please try again.'));
      setStep('select');
    } finally {
      setLoading(false);
    }
  };

  const runBulkDecrypt = async () => {
    const group = selectedGroupData;
    if (!group || !wasmModule) {
      setError('The decryption module is still loading. Please wait a moment and try again.');
      setStep('select');
      return;
    }

    const txsToDecrypt = scopedTxs;
    if (txsToDecrypt.length === 0) {
      setError('No transactions match the selected filters.');
      setStep('select');
      return;
    }

    const amountsMap = {};
    let received = 0;
    let sent = 0;
    const total = txsToDecrypt.length;
    setDecryptProgress({ current: 0, total });

    const parseAmountValue = (displayAmount) => {
      if (displayAmount == null || typeof displayAmount !== 'string') return NaN;
      const trimmed = displayAmount.trim();
      const firstSpace = trimmed.indexOf(' ');
      const numStr = firstSpace >= 0 ? trimmed.slice(0, firstSpace) : trimmed;
      const value = parseFloat(numStr);
      return Number.isFinite(value) ? value : NaN;
    };

    for (let i = 0; i < txsToDecrypt.length; i++) {
      const tx = txsToDecrypt[i];
      setDecryptProgress({ current: i + 1, total });
      setStep('decrypting');
      try {
        const getIbeKey = async (_identity, meta = {}) => {
          const pepIdentity = meta.pepIdentity || _identity;
          return ensureIbeKey(pepIdentity, { which: meta.which, tx });
        };

        const { displayAmount } = await decryptTransactionAmount(
          tx,
          userAddress,
          getIbeKey,
          wasmModule
        );
        amountsMap[tx.id] = displayAmount;
        const value = parseAmountValue(displayAmount);
        if (!Number.isNaN(value)) {
          const role = getRoleForUser(tx, userAddress);
          if (role === 'sender') sent += value;
          else received += value;
        }
      } catch (err) {
        console.error(`Decrypt failed for tx ${tx.id}:`, err);
        amountsMap[tx.id] = 'decryption error';
      }
    }

    setResult({
      amountsMap,
      received: roundDecimal(received),
      sent: roundDecimal(sent),
      net: roundDecimal(received - sent),
      chainName: group.chainName,
      token: group.token,
      application: selectedApplication
        ? getApplicationDisplayName(selectedApplication)
        : null,
      count: txsToDecrypt.length
    });
    setStep('complete');
    setLoading(false);
  };

  const handleChainChange = (value) => {
    setSelectedChainId(value === '' ? null : value);
    setSelectedToken(null);
    setSelectedApplication(null);
  };

  const handleTokenChange = (value) => {
    setSelectedToken(value === '' ? null : value);
    setSelectedApplication(null);
  };

  const handleApplicationChange = (value) => {
    setSelectedApplication(value === '' ? null : value);
  };

  const chainSelectOptions = [
    { value: '', label: 'Select chain...', icon: null },
    ...chains.map((c) => ({ value: c.chainId, label: c.chainName, icon: null }))
  ];

  const tokenSelectOptions = [
    { value: '', label: 'Select token...', icon: null },
    ...tokensForSelectedChain.map(({ token, count }) => ({
      value: token,
      label: `${token} (${count} transaction${count !== 1 ? 's' : ''})`,
      icon: null
    }))
  ];

  const applicationSelectOptions = [
    {
      value: '',
      label: selectedGroupData
        ? `All applications (${selectedGroupData.txs.length} transaction${selectedGroupData.txs.length !== 1 ? 's' : ''})`
        : 'All applications',
      icon: null
    },
    ...applicationsForSelectedGroup.map(({ application, label, count }) => ({
      value: application,
      label: `${label} (${count} transaction${count !== 1 ? 's' : ''})`,
      icon: null
    }))
  ];

  const handleStartRequest = () => {
    if (!selectedGroup || scopedTxs.length === 0) return;
    setStep('request');
    setError(null);
    handleRequestKey();
  };

  const handleDone = () => {
    if (result?.amountsMap) {
      onBulkDecryptionComplete(result.amountsMap);
    }
    onClose();
  };

  const escapeCsvCell = (value) => {
    if (value == null || value === '') return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const handleDownloadAccumulatedCsv = () => {
    if (!result) return;
    const groupTxs = scopedTxs;
    const txHeaders = ['Chain', 'Token', 'Application', 'Date', 'Type', 'From', 'To', 'Tx Hash', 'Amount'];
    const rows = [txHeaders.map(escapeCsvCell).join(',')];
    for (const tx of groupTxs) {
      const sender =
        tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
          ? (tx.details?.sender || 'N/A')
          : (tx.userAddress || tx.details?.sender || 'N/A');
      const receiver =
        tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
          ? (tx.userAddress || tx.details?.recipient || 'N/A')
          : (tx.details?.recipient || 'N/A');
      const role = getRoleForUser(tx, userAddress);
      const typeLabel = role === 'sender' ? 'Sent' : 'Received';
      const amount = result.amountsMap[tx.id] ?? 'decryption error';
      rows.push(
        [
          result.chainName,
          result.token,
          getApplicationDisplayName(getRequestApplication(tx)),
          formatDate(tx.createdAt),
          typeLabel,
          sender,
          receiver,
          tx.transactionHash || 'N/A',
          amount
        ].map(escapeCsvCell).join(',')
      );
    }
    rows.push(
      ['', '', '', 'Summary', '', '', '', 'Received', `${result.received} ${result.token}`].map(escapeCsvCell).join(',')
    );
    rows.push(
      ['', '', '', '', '', '', '', 'Sent', `${result.sent} ${result.token}`].map(escapeCsvCell).join(',')
    );
    rows.push(
      ['', '', '', '', '', '', '', 'Net', `${result.net} ${result.token}`].map(escapeCsvCell).join(',')
    );
    const csv = rows.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const appPart = result.application
      ? `-${result.application.replace(/\s+/g, '-')}`
      : '';
    a.download = `aggregated-${result.chainName.replace(/\s+/g, '-')}-${result.token}${appPart}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const busy = loading || step === 'request' || step === 'polling' || step === 'decrypting';
  const failureCount = result
    ? Object.values(result.amountsMap || {}).filter((v) => String(v) === 'decryption error').length
    : 0;

  return (
    <Modal
      title="Aggregate amounts"
      onClose={onClose}
      dismissible={!busy}
    >
      {userAddress && (
        <p className="accumulation-user-caption" style={{ marginTop: 0 }}>
          {userAddress.slice(0, 6)}…{userAddress.slice(-4)}
        </p>
      )}
          {step === 'select' && (
            <div className="decryption-step">
              <h3>Select filters</h3>
              <p className="step-description">
                Choose a chain and token to request decryption keys for matching transactions for this user. Application is optional.
              </p>
              {error && <div className="error-message" role="alert">{error}</div>}
              <div className="accumulation-selects">
                <div className="accumulation-filter-group">
                  <label htmlFor="accumulation-chain">Chain</label>
                  <SelectField
                    id="accumulation-chain"
                    value={selectedChainId ?? ''}
                    onChange={handleChainChange}
                    options={chainSelectOptions}
                    placeholder="Select chain..."
                  />
                </div>
                <div className="accumulation-filter-group">
                  <label htmlFor="accumulation-token">Token</label>
                  <SelectField
                    id="accumulation-token"
                    value={selectedToken ?? ''}
                    onChange={handleTokenChange}
                    options={tokenSelectOptions}
                    disabled={selectedChainId == null}
                    placeholder="Select token..."
                  />
                </div>
                <div className="accumulation-filter-group">
                  <label htmlFor="accumulation-application">Application <span className="optional-label">(optional)</span></label>
                  <SelectField
                    id="accumulation-application"
                    value={selectedApplication ?? ''}
                    onChange={handleApplicationChange}
                    options={applicationSelectOptions}
                    disabled={!selectedGroup}
                    placeholder="All applications"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={onClose} className="button-secondary">Cancel</button>
                <button
                  type="button"
                  onClick={handleStartRequest}
                  className="button-primary"
                  disabled={!selectedGroup || scopedTxs.length === 0 || loading || !wasmModule}
                >
                  {!wasmModule ? 'Loading…' : 'Request key & decrypt all'}
                </button>
              </div>
            </div>
          )}

          {(step === 'request' || step === 'polling') && (
            <div className="decryption-step">
              <div className="polling-state busy-state" role="status" aria-live="polite">
                <div className="loading-spinner" aria-hidden="true"></div>
                <h3>{step === 'request' ? 'Requesting decryption key…' : 'Waiting for decryption key'}</h3>
                <p className="step-description">
                  The key request has been submitted. Once ready, all transactions for {scopeLabel} will be decrypted.
                </p>
                {pollingStatus && (
                  <div className="polling-status">
                    <p>Elapsed time: {(pollingStatus.elapsed / 1000).toFixed(0)}s</p>
                  </div>
                )}
                {error && <div className="error-message" role="alert">{error}</div>}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => {
                      pollAbortRef.current?.abort();
                      setStep('select');
                      setLoading(false);
                      setError(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'decrypting' && (
            <div className="decryption-step">
              <div className="decrypting-state busy-state" role="status" aria-live="polite">
                <div className="loading-spinner" aria-hidden="true"></div>
                <p>Decrypting all transactions…</p>
                <p className="decrypting-steps">
                  {decryptProgress.current} / {decryptProgress.total} complete
                </p>
              </div>
            </div>
          )}

          {step === 'complete' && result && (
            <div className="decryption-step">
              <div className="success-state" role="status" aria-live="polite">
                <div className="success-icon" aria-hidden="true" />
                <h3>Aggregation complete</h3>
                {failureCount > 0 && (
                  <div className="ui-alert ui-alert--warning" role="status">
                    {failureCount} transaction{failureCount === 1 ? '' : 's'} could not be decrypted.
                  </div>
                )}
                <div className="accumulation-result">
                  <div className="result-row">
                    <span className="result-label">Received</span>
                    <span className="result-value received">{result.received.toLocaleString(undefined, { maximumFractionDigits: 6 })} {result.token}</span>
                  </div>
                  <div className="result-row">
                    <span className="result-label">Sent</span>
                    <span className="result-value sent">{result.sent.toLocaleString(undefined, { maximumFractionDigits: 6 })} {result.token}</span>
                  </div>
                  <div className="result-row net">
                    <span className="result-label">Net</span>
                    <span className={`result-value net ${result.net >= 0 ? 'positive' : 'negative'}`}>
                      {result.net >= 0 ? '' : '−'}{Math.abs(result.net).toLocaleString(undefined, { maximumFractionDigits: 6 })} {result.token}
                    </span>
                  </div>
                </div>
                <p className="result-footer">
                  {result.count} transaction{result.count !== 1 ? 's' : ''} decrypted
                  {result.application ? ` for ${result.application}` : ''}.
                  {' '}Amounts are now visible in the table.
                </p>
                <div className="modal-actions">
                  <button type="button" onClick={handleDownloadAccumulatedCsv} className="button-secondary">
                    Download totals
                  </button>
                  <button type="button" onClick={handleDone} className="button-primary">
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
    </Modal>
  );
};

export default AccumulationModal;
